// src/pages/DebtStudio/components/Reports/DebtServiceTable.jsx
import React, { useMemo, useState } from "react";
import ExcelGrid from "components/ExcelGrid";
import dayjs from "lib/dayjs";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { buildPrincipalPeriods } from "pages/DebtStudio/lib/buildPrincipalPeriods";

/**
 * DebtServiceTable
 * --------------------------------------------
 * Displays the computed debt service schedule with period totals.
 * The user can toggle between Principal-Period and Fiscal-Year views.
 *
 * NOTE: This component intentionally does NOT render its own "card" container.
 * It is meant to live inside the parent "Debt Service" card.
 */
export default function DebtServiceTable({ rows = [], form = undefined }) {
  const [groupMode, setGroupMode] = useState("period");

  const { form: ctxForm } = useDebtStudio();
  const effectiveForm = form ?? ctxForm ?? {};
  const schedule = useMemo(() => rows || [], [rows]);

  const firstPrincipalDate =
    effectiveForm?.firstPrincipalDate || effectiveForm?.first_principal_date;
  const fiscalYearEnd =
    effectiveForm?.fiscalYearEnd || effectiveForm?.fiscal_year_end || "";
  const datedDate = effectiveForm?.datedDate || effectiveForm?.dated_date;
  const principalFrequency =
    effectiveForm?.principalFrequency || effectiveForm?.principal_frequency || "annual";
  const isLoan =
    String(effectiveForm?.financingType || effectiveForm?.financing_type || "")
      .trim()
      .toLowerCase() === "loan";

  /** ---------- Formatters ---------- */
  const moneyFormat = (v) =>
    v != null
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";

  const rateFormat = (v, row) => {
    if (v == null || v === "") return "";

    const rate = Number(v);
    const interest = Number(row?.interest);

    if (
      !Number.isFinite(rate) ||
      !Number.isFinite(interest) ||
      Math.abs(interest) <= 0.005
    ) {
      return "";
    }

    return `${rate.toFixed(3)}%`;
  };

  const rightAlign = "text-right numeric";

  /** ---------- Utility ---------- */
  const maxDate = (rows) => {
    let max = null;
    for (const r of rows) {
      const d = dayjs(r.date);
      if (!d.isValid()) continue;
      if (!max || d.isAfter(max)) max = d;
    }
    return max;
  };

  function getAdjustment(row) {
    return Number(row?.debtServiceAdjustment ?? row?.adjustment ?? 0) || 0;
  }

  function getNetDebtService(row) {
    const explicit = Number(row?.netDebtService);
    if (Number.isFinite(explicit)) return explicit;

    const gross = Number(row?.total) || 0;
    return gross + getAdjustment(row);
  }

  function getExistingCashFlows(row) {
    const explicit = Number(
      row?.existingCashFlows ??
        row?.existingDebtService ??
        row?.baseDebtService ??
        0
    );

    return Number.isFinite(explicit) ? explicit : 0;
  }

  function getAggregateCashFlows(row) {
    const explicit = Number(
      row?.aggregateCashFlows ??
        row?.aggregateDebtService ??
        row?.aggregateNetDebtService
    );

    if (Number.isFinite(explicit)) return explicit;

    return getNetDebtService(row) + getExistingCashFlows(row);
  }

  function hasNonzeroAdjustment(rows = []) {
    return rows.some((r) => Math.abs(getAdjustment(r)) > 0.005);
  }

  function hasExistingCashFlows(rows = []) {
    return rows.some((r) => Math.abs(getExistingCashFlows(r)) > 0.005);
  }

  function buildAnnualPeriods(startDate, periodEndAnchor, lastCashflowDate) {
    return buildPrincipalPeriods({
      startDate,
      periodEndAnchor,
      lastCashflowDate,
      principalFrequency: "annual",
    });
  }

  /** ---------- Principal-Period Grouping ---------- */
  function groupByPrincipalPeriod(
    cashflows,
    originalDatedDate,
    anchorEnd,
    principalFrequencyValue
  ) {
    if (!cashflows?.length || !anchorEnd || !originalDatedDate)
      return cashflows.map((r) => ({ ...r, periodTotal: null }));

    const last = maxDate(cashflows);
    const allPeriods = buildPrincipalPeriods({
      startDate: originalDatedDate,
      periodEndAnchor: anchorEnd,
      lastCashflowDate: last,
      principalFrequency: principalFrequencyValue,
    });

    if (!allPeriods.length) return cashflows.map((r) => ({ ...r, periodTotal: null }));

    const periods = allPeriods;

    const totals = periods.map((p) => ({
      start: p.start,
      end: p.end,
      aggregateTotal: 0,
      lastRowIndex: -1,
    }));

    const out = cashflows.map((r) => {
      const existing = getExistingCashFlows(r);

      return {
        ...r,
        existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
        aggregateCashFlows: getAggregateCashFlows(r),
        periodTotal: null,
      };
    });

    for (let i = 0; i < out.length; i++) {
      const row = out[i];
      const payDate = dayjs(row.date);
      const idx = totals.findIndex(
        (p) =>
          payDate.isAfter(p.start, "day") &&
          (payDate.isBefore(p.end, "day") || payDate.isSame(p.end, "day"))
      );

      if (idx >= 0) {
        totals[idx].aggregateTotal += getAggregateCashFlows(row);
        totals[idx].lastRowIndex = i;
      }
    }

    for (const p of totals) {
      if (p.lastRowIndex >= 0) {
        out[p.lastRowIndex] = {
          ...out[p.lastRowIndex],
          periodTotal: p.aggregateTotal,
        };
      } else {
        out.push({
          date: p.end.format("YYYY-MM-DD"),
          principal: null,
          interest: null,
          total: null,
          debtServiceAdjustment: null,
          adjustment: null,
          netDebtService: null,
          existingCashFlows: null,
          aggregateCashFlows: null,
          periodTotal: 0,
        });
      }
    }

    out.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
    return out;
  }

  /** ---------- Fiscal-Year Grouping ---------- */
  function groupByFiscalYear(cashflows, originalDatedDate, fiscalYearEnd) {
    if (!cashflows?.length || !fiscalYearEnd || !originalDatedDate)
      return cashflows.map((r) => ({ ...r, periodTotal: null }));

    const lastPayment = maxDate(cashflows);
    const allPeriods = buildAnnualPeriods(originalDatedDate, fiscalYearEnd, lastPayment);
    if (!allPeriods.length) return cashflows.map((r) => ({ ...r, periodTotal: null }));

    const periods = allPeriods;

    const out = cashflows.map((r) => {
      const existing = getExistingCashFlows(r);

      return {
        ...r,
        existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
        aggregateCashFlows: getAggregateCashFlows(r),
        periodTotal: null,
      };
    });

    for (const p of periods) {
      const payments = cashflows.filter((r) => {
        const d = dayjs(r.date);
        return (
          d.isAfter(p.start, "day") &&
          (d.isBefore(p.end, "day") || d.isSame(p.end, "day"))
        );
      });

      const periodTotal = payments.reduce(
        (sum, r) => sum + getAggregateCashFlows(r),
        0
      );

      out.push({
        date: p.end.format("YYYY-MM-DD"),
        principal: null,
        interest: null,
        total: null,
        debtServiceAdjustment: null,
        adjustment: null,
        netDebtService: null,
        existingCashFlows: null,
        aggregateCashFlows: null,
        periodTotal,
        isFiscalTotal: true,
      });
    }

    out.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
    return out;
  }

  /** ---------- Derived Schedule ---------- */
  const groupedSchedule = useMemo(() => {
    if (groupMode === "period") {
      return groupByPrincipalPeriod(
        schedule,
        datedDate,
        firstPrincipalDate,
        principalFrequency
      );
    }

    if (groupMode === "fiscal") {
      return groupByFiscalYear(schedule, datedDate, fiscalYearEnd);
    }

    return schedule.map((r) => {
      const existing = getExistingCashFlows(r);

      return {
        ...r,
        existingCashFlows: Math.abs(existing) > 0.005 ? existing : null,
        aggregateCashFlows: getAggregateCashFlows(r),
        periodTotal: null,
      };
    });
  }, [
    schedule,
    groupMode,
    datedDate,
    firstPrincipalDate,
    principalFrequency,
    fiscalYearEnd,
  ]);

  const showAdjustmentColumns = useMemo(
    () => hasNonzeroAdjustment(groupedSchedule),
    [groupedSchedule]
  );

  const showExistingCashFlowColumns = useMemo(
    () => hasExistingCashFlows(groupedSchedule),
    [groupedSchedule]
  );

  /** ---------- Toggle Control ---------- */
  const Toggle = () => (
    <div className="inline-flex items-center rounded-md border border-border overflow-hidden">
      {["period", "fiscal"].map((mode) => {
        const active = groupMode === mode;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => setGroupMode(mode)}
            aria-pressed={active}
            className={
              "px-3 py-1 text-sm font-medium transition-colors " +
              (active
                ? "btn btn-primary"
                : "bg-app text-muted hover:text-app hover:bg-app/10")
            }
          >
            {mode === "period" ? "Period" : "Fiscal Year"}
          </button>
        );
      })}
    </div>
  );

  /** ---------- Columns ---------- */
  const columns = [
    {
      key: "date",
      label: "Date",
      width: "w-40",
      className: "numeric",
    },
    ...(isLoan
      ? [
          {
            key: "beginningBalance",
            label: "Beginning Balance",
            numeric: true,
            width: "w-44",
            className: rightAlign,
            headerClassName: "text-right",
            format: moneyFormat,
          },
          {
            key: "rate",
            label: "Rate",
            numeric: true,
            width: "w-24",
            className: rightAlign,
            headerClassName: "text-right",
            format: rateFormat,
          },
        ]
      : []),
    {
      key: "principal",
      label: "Principal",
      numeric: true,
      width: "w-32",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
    {
      key: "interest",
      label: "Interest",
      numeric: true,
      width: "w-32",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
    {
      key: "total",
      label: "Debt Service",
      numeric: true,
      width: "w-32",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
    ...(showAdjustmentColumns
      ? [
          {
            key: "debtServiceAdjustment",
            label: "Adjustments",
            numeric: true,
            width: "w-36",
            className: rightAlign,
            headerClassName: "text-right",
            format: moneyFormat,
          },
          {
            key: "netDebtService",
            label: "Net Debt Service",
            numeric: true,
            width: "w-44",
            className: rightAlign,
            headerClassName: "text-right",
            format: moneyFormat,
          },
        ]
      : []),
    ...(showExistingCashFlowColumns
      ? [
          {
            key: "existingCashFlows",
            label: "Existing D/S",
            numeric: true,
            width: "w-44",
            className: rightAlign,
            headerClassName: "text-right",
            format: moneyFormat,
          },
        ]
      : []),
    {
      key: "periodTotal",
      label: groupMode === "period" ? "Period Total" : "Fiscal Year Total",
      numeric: true,
      width: "w-44",
      className: rightAlign,
      headerClassName: "text-right",
      format: moneyFormat,
    },
  ];

  /** ---------- Render ---------- */
  if (!schedule?.length) {
    return <div className="p-4 text-muted">No debt service schedule available.</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Toggle />
        </div>
      </div>

      <ExcelGrid columns={columns} rows={groupedSchedule} readOnly />
    </div>
  );
}
